import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate, Outlet } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { OnboardingTour, useOnboarding } from '../../components/OnboardingTour';
import { NotificationBell } from '../../components/NotificationBell';
import { LanguageSelector } from '../../components/LanguageSelector';
import {
  LayoutDashboard,
  Briefcase,
  Users,
  Package,
  FileText,
  DollarSign,
  Target,
  Settings,
  LogOut,
  Menu,
  X,
  ChevronDown,
  Scale,
  Calendar,
  Wrench,
  Layers,
  HelpCircle,
  History,
  Palette,
  BarChart3,
  MessageSquare,
  Shield,
  Truck,
  Send,
  ClipboardList,
  ListChecks,
  Landmark
} from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_jax-craft-remodel/artifacts/6r3h6sd1_Transparent%20Logo.png";

export const PortalLayout = () => {
  const { user, logout, isCOO, api } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [unreadMessages, setUnreadMessages] = useState(0);
  
  // Onboarding tour
  const userRole = user?.role || 'coo';
  const { shouldShow: showOnboarding, setShouldShow: setShowOnboarding } = useOnboarding(userRole);

  // Fetch unread message count
  useEffect(() => {
    const fetchUnreadCount = async () => {
      try {
        const response = await api.get('/messages/conversations');
        const total = response.data.reduce((sum, conv) => sum + (conv.unread_count || 0), 0);
        setUnreadMessages(total);
      } catch (error) {
        console.error('Error fetching unread messages:', error);
      }
    };
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 30000); // Refresh every 30s
    return () => clearInterval(interval);
  }, [api]);

  const handleLogout = () => {
    logout();
    navigate('/portal/login');
  };

  const navigation = [
    { name: 'Dashboard', href: '/portal', icon: LayoutDashboard },
    { name: 'Jobs Pipeline', href: '/portal/jobs', icon: Briefcase },
    { name: 'Project Progress', href: '/portal/progress', icon: ListChecks },
    ...(isCOO ? [
      { name: 'Project Management', href: '/portal/project-management', icon: ClipboardList },
    ] : []),
    { name: 'Proposal Builder', href: '/portal/proposal-builder', icon: FileText },
    { name: 'Clients', href: '/portal/clients', icon: Users },
    { name: 'Team Builder', href: '/portal/team', icon: Users },
    { name: 'Contractors', href: '/portal/contractors', icon: Users },
    { name: 'Inventory', href: '/portal/inventory', icon: Package },
    { name: 'Vehicle Fleet', href: '/portal/vehicles', icon: Truck },
    { name: 'Floor Plans', href: '/portal/floor-plans', icon: Layers },
    { name: 'Finish Library', href: '/portal/finish-library', icon: Palette },
    { name: 'Maintenance', href: '/portal/maintenance', icon: Wrench },
    { name: 'Calendar', href: '/portal/calendar', icon: Calendar },
    { name: 'Documents', href: '/portal/documents', icon: FileText },
    { name: 'Messages', href: '/portal/messages', icon: Send, badge: unreadMessages },
    { name: 'Reviews', href: '/portal/reviews', icon: MessageSquare },
    { name: 'Reports', href: '/portal/reports', icon: BarChart3 },
    { name: 'Financial OS', href: '/portal/financial-os', icon: Landmark },
    ...(isCOO ? [
      { name: 'Accounting', href: '/portal/accounting', icon: DollarSign },
      { name: 'Liabilities', href: '/portal/liabilities', icon: Shield },
      { name: 'Business Plan', href: '/portal/business-plan', icon: Target },
      { name: 'Legal', href: '/portal/legal', icon: Scale },
    ] : []),
    { name: 'Settings', href: '/portal/settings', icon: Settings },
    { name: 'Help', href: '/portal/help', icon: HelpCircle },
  ];

  const isActive = (href) => {
    if (href === '/portal') {
      return location.pathname === '/portal';
    }
    return location.pathname.startsWith(href);
  };

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Sidebar - Desktop */}
      <aside className="hidden lg:flex lg:flex-col lg:w-64 lg:fixed lg:inset-y-0 bg-[#0A0A0A]">
        {/* Logo */}
        <div className="flex items-center h-16 px-6 border-b border-white/10">
          <Link to="/portal">
            <img src={LOGO_URL} alt="Jax Remodeling" className="h-10 w-auto" />
          </Link>
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-4 py-6 space-y-1 overflow-y-auto">
          {navigation.map((item) => (
            <Link
              key={item.name}
              to={item.href}
              className={`flex items-center gap-3 px-4 py-3 text-sm font-medium transition-colors ${
                isActive(item.href)
                  ? 'bg-[#C6A87C]/10 text-[#C6A87C] border-l-2 border-[#C6A87C]'
                  : 'text-gray-400 hover:text-white hover:bg-white/5'
              }`}
            >
              <item.icon className="w-5 h-5" />
              <span className="flex-1">{item.name}</span>
              {item.badge > 0 && (
                <span className="px-2 py-0.5 text-xs font-bold bg-red-500 text-white rounded-full">
                  {item.badge > 99 ? '99+' : item.badge}
                </span>
              )}
            </Link>
          ))}
        </nav>

        {/* User Section */}
        <div className="p-4 border-t border-white/10">
          <div className="flex items-center gap-3 px-4 py-3">
            <div className="w-10 h-10 bg-[#C6A87C]/20 flex items-center justify-center text-[#C6A87C] font-semibold">
              {user?.name?.charAt(0)}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-white text-sm font-medium truncate">{user?.name}</p>
              <p className="text-gray-500 text-xs truncate">{user?.title}</p>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="flex items-center gap-3 w-full px-4 py-2 mt-2 text-sm text-gray-400 hover:text-white hover:bg-white/5 transition-colors"
          >
            <LogOut className="w-4 h-4" />
            Sign Out
          </button>
        </div>
      </aside>

      {/* Mobile Sidebar */}
      {sidebarOpen && (
        <div className="lg:hidden fixed inset-0 z-50">
          <div className="absolute inset-0 bg-black/50" onClick={() => setSidebarOpen(false)} />
          <aside className="absolute left-0 top-0 bottom-0 w-64 bg-[#0A0A0A]">
            <div className="flex items-center justify-between h-16 px-6 border-b border-white/10">
              <img src={LOGO_URL} alt="Jax Remodeling" className="h-10 w-auto" />
              <button onClick={() => setSidebarOpen(false)} className="text-white">
                <X className="w-6 h-6" />
              </button>
            </div>
            <nav className="px-4 py-6 space-y-1">
              {navigation.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  onClick={() => setSidebarOpen(false)}
                  className={`flex items-center gap-3 px-4 py-3 text-sm font-medium transition-colors ${
                    isActive(item.href)
                      ? 'bg-[#C6A87C]/10 text-[#C6A87C]'
                      : 'text-gray-400 hover:text-white'
                  }`}
                >
                  <item.icon className="w-5 h-5" />
                  {item.name}
                </Link>
              ))}
            </nav>
          </aside>
        </div>
      )}

      {/* Main Content */}
      <div className="lg:pl-64 flex-1">
        {/* Top Header */}
        <header className="sticky top-0 z-40 h-16 bg-white border-b border-gray-100 flex items-center justify-between px-4 lg:px-8">
          {/* Mobile Menu Button */}
          <button
            onClick={() => setSidebarOpen(true)}
            className="lg:hidden p-2 text-gray-500 hover:text-gray-700"
          >
            <Menu className="w-6 h-6" />
          </button>

          {/* Breadcrumb / Page Title */}
          <div className="hidden lg:block">
            <Link to="/" className="text-sm text-gray-400 hover:text-[#C6A87C]">
              ← Back to Website
            </Link>
          </div>

          {/* Right Side Actions */}
          <div className="flex items-center gap-3">
            {/* Quick Message Button */}
            <Link
              to="/portal/messages"
              className="relative p-2 text-gray-500 hover:text-[#C6A87C] hover:bg-[#C6A87C]/10 rounded-lg transition-colors"
              title="Messages"
              data-testid="header-messages-btn"
            >
              <Send className="w-5 h-5" />
              {unreadMessages > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 flex items-center justify-center text-xs font-bold bg-red-500 text-white rounded-full">
                  {unreadMessages > 9 ? '9+' : unreadMessages}
                </span>
              )}
            </Link>
            
            {/* Language Selector */}
            <LanguageSelector />
            
            {/* Notification Bell */}
            <NotificationBell userType="owner" />
            
            {/* User Menu */}
            <div className="relative">
            <button
              onClick={() => setUserMenuOpen(!userMenuOpen)}
              className="flex items-center gap-3 px-3 py-2 hover:bg-gray-50 rounded-lg transition-colors"
            >
              <div className="w-8 h-8 bg-[#C6A87C]/10 flex items-center justify-center text-[#C6A87C] font-semibold text-sm">
                {user?.name?.charAt(0)}
              </div>
              <span className="hidden md:block text-sm font-medium">{user?.name}</span>
              <ChevronDown className="w-4 h-4 text-gray-400" />
            </button>

            {userMenuOpen && (
              <>
                <div className="fixed inset-0 z-10" onClick={() => setUserMenuOpen(false)} />
                <div className="absolute right-0 mt-2 w-56 bg-white border border-gray-100 shadow-lg z-20">
                  <div className="p-4 border-b border-gray-100">
                    <p className="font-medium">{user?.name}</p>
                    <p className="text-sm text-gray-500">{user?.email}</p>
                    <span className={`inline-block mt-2 px-2 py-1 text-xs font-semibold ${
                      isCOO ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'
                    }`}>
                      {user?.title} {isCOO ? '(COO)' : user?.role === 'president' ? '(President)' : ''}
                    </span>
                    {isCOO && (
                      <p className="text-xs text-gray-400 mt-1">Chief Operating Officer</p>
                    )}
                  </div>
                  <div className="p-2">
                    <Link
                      to="/portal/settings"
                      onClick={() => setUserMenuOpen(false)}
                      className="flex items-center gap-2 px-3 py-2 text-sm text-gray-600 hover:bg-gray-50"
                    >
                      <Settings className="w-4 h-4" />
                      Settings
                    </Link>
                    <button
                      onClick={handleLogout}
                      className="flex items-center gap-2 w-full px-3 py-2 text-sm text-gray-600 hover:bg-gray-50"
                    >
                      <LogOut className="w-4 h-4" />
                      Sign Out
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="p-4 lg:p-8">
          <Outlet />
        </main>
        
        {/* Floating Messages Button */}
        {location.pathname !== '/portal/messages' && (
          <Link
            to="/portal/messages"
            className="fixed bottom-6 right-6 lg:bottom-8 lg:right-8 z-40 flex items-center gap-2 px-4 py-3 bg-[#C6A87C] text-white rounded-full shadow-lg hover:bg-[#B09265] transition-all hover:scale-105"
            data-testid="floating-messages-btn"
          >
            <Send className="w-5 h-5" />
            <span className="hidden sm:inline font-medium">Messages</span>
            {unreadMessages > 0 && (
              <span className="absolute -top-2 -right-2 w-6 h-6 flex items-center justify-center text-xs font-bold bg-red-500 text-white rounded-full animate-pulse">
                {unreadMessages > 99 ? '99+' : unreadMessages}
              </span>
            )}
          </Link>
        )}
      </div>

      {/* Onboarding Tour */}
      {showOnboarding && (
        <OnboardingTour 
          role={userRole} 
          onComplete={() => setShowOnboarding(false)} 
        />
      )}
    </div>
  );
};
